import ExternalTimelyRoute from "./apps/plany-care/integrations/booking/ExternalTimelyRoute";
// RouteGroups.tsx (drop-in replacement)
// Clean, commented, and aligned to canonical /b/:businessUrl

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import jwtDecode from "jwt-decode";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useMutation, useQuery } from "@apollo/client";
import { Box, Button, Chip, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { isConciergeUser } from "./platform/util/conciergeAccess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EventIcon from "@mui/icons-material/Event";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

import { BusinessQueryNames, GET_BUSINESS_BY_URL, GET_BUSINESS_CATEGORIES, GET_TOKEN } from "./platform/graphql/queries";
import { GET_JOB_POSTINGS_BY_BUSINESS } from "./platform/graphql/queries/careJobs";

// Business / Customer
import BusinessPortal from "./platform/pages/business/businessPage/businessPortal";
import CategoryPage from "./platform/pages/customer/categoryPage";
import PricingPage from "./platform/pages/business/pricingPage.jsx";
import ForgotPassword from "./platform/components/auth/forgotPassword";
import { RegisterBusinessDialog } from "./platform/components/business/business/businessSettings";

// Calendar & booking


// Auth & user
import Login from "./platform/components/auth/login/login";
import SignUp from "./platform/components/auth/signup";
import UserProfilePage from "./platform/pages/userPortal/userPortal";
import UpdateStaffStatus from "./products/staffCaregiverHiring/components/business/staff/updateStaffStatus";







import { TipPage, BuyCoinsPage, WalletDashboard, TipProvider } from "./products/wallet";
import {
  ProviderAgreementPage,
  ReferrerLandingPage,
  ReferrerPortalPage,
  RequestOrReferralPage,
  ConciergeCityBoardPage,
  AgentCompAgreementPage,
  AgentApplicationPage,
  ConciergeAdminDashboardPage,
  PlanyAdminDashboardPage,
  BroadcastGroupPage,
} from "./products/referralCareDirectory";
import CareDashboardProductPage from "./products/care/pages/CareDashboardProductPage";
import CareFindPage from "./products/care/pages/CareFindPage";
import CareSchedulePortal from "./apps/plany-care/pages/CareSchedulePortal";
import CareBrowsePage from "./products/care/pages/CareBrowsePage";
import CareSetupRequiredPage from "./products/care/pages/CareSetupRequiredPage";








import {
  StaffPortal,
  JobBoardPage,
  JobDetailPage,
  JobSearchLandingPage,
  JobPostLandingPage,
  CareJobPricingPage,
  JobManagePortal,
  MyJobsPage,
  ClaimResume,
  MarketplacePortal,
  MarketplacePage,
  MarketplaceCreatePage,
} from "./products/staffCaregiverHiring";
import { ResidentPortal } from "./products/residentManagementEmar";
import ResidentManagementPublicLandingPage from "./products/residentManagementEmar/pages/ResidentManagementPublicLandingPage";

// Common & misc
import Error from "./platform/components/common/error";
import SearchPage from "./platform/components/consumer/search/searchPage";
import Main from "./platform/components/common/main";
import PrivacyTermsAbout from "./platform/pages/privacy/privacyTermsAbout";
import SubscriptionSuccessPage from "./platform/pages/business/subscriptionSuccessPage";

// Routing helpers
import ValidateBusinessUrl from "./platform/util/validateBusinessId";
import RootRedirect from "./platform/routes/rootRedirect";
import VerticalLanding from "./platform/routes/verticalLanding";
import UserPage from "./platform/pages/business/userPage/userPage";
import BusinessRegisterPublicPage from "./platform/pages/public/BusinessRegisterPublicPage";
import WebDesignPage from "./platform/pages/public/WebDesignPage";
import RequireProviderAgreement from "./platform/guards/requireProviderAgreement";
import BillingSuccess from "./platform/components/common/strip/billingSucess";
import BillingCancel from "./platform/components/common/strip/billingCancel";
import { isPathProductEnabled } from "./products/config/productFlags";
import { setSelectedBusiness } from "./platform/redux/actions/businessActions";
import useLoadReduxStoreData from "./platform/redux/reducers/loadReduxStoreData";
import { useSelectBusinesses, useSelectedBusiness } from "./platform/redux/store";
import { UPDATE_DEFAULT_BUSINESS } from "./platform/graphql/mutations";
import { setToken as persistToken } from "./platform/util/authUtils";
import {
  hasCareWorkspaceAccess,
  hasStandaloneBookingAccess,
  isProductSubscribed,
  isProductVisible,
} from "./core/business/productAccess";
import {
  isCareBusinessEligible,
  isCareCategoryName,
} from "./core/business/category";
import { detectVerticalFromPath, isVerticalId } from "./platform/verticals/registry";
import { getStoredVertical } from "./platform/routes/verticalPref";
import { RESERVED_TOP_LEVEL_BUSINESS_SLUGS } from "./platform/util/businessSlug";
import {
  getRuntimeAppProfile,
  getRuntimeBusinessHomePath,
  getCrossAppUrl,
  getRuntimePublicHomePath,
} from "./platform/runtime/appProfile";

const LOCAL_WORKSPACE_PRODUCTS_KEY = "planyLocalWorkspaceProducts";
const CARE_BRAND_HOSTS = new Set(["care.plany365.com"]);
function isCareBrandHost() {
  if (typeof window === "undefined") return false;
  const hostname = String(window.location.hostname || "").trim().toLowerCase();
  return CARE_BRAND_HOSTS.has(hostname);
}

function readLocalWorkspaceProducts(businessId) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_WORKSPACE_PRODUCTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const guestProducts = parsed.guest || {};
    const businessProducts = businessId ? parsed[String(businessId)] || {} : {};
    return { ...guestProducts, ...businessProducts };
  } catch {
    return {};
  }
}

function hasLocalWorkspaceProduct(providerUser, productId) {
  const businessId = Number(providerUser?.defaultBusinessId || 0) || null;
  const localWorkspaceProducts = readLocalWorkspaceProducts(businessId);
  return Boolean(localWorkspaceProducts?.[productId]);
}

function hasLocalWorkspaceOverride(providerUser, productId) {
  const businessId = Number(providerUser?.defaultBusinessId || 0) || null;
  const localWorkspaceProducts = readLocalWorkspaceProducts(businessId);
  return Object.prototype.hasOwnProperty.call(localWorkspaceProducts, productId);
}

function hasWorkspaceProductAccess(providerUser, productId) {
  if (hasLocalWorkspaceOverride(providerUser, productId)) {
    return hasLocalWorkspaceProduct(providerUser, productId);
  }

  return (
    isProductSubscribed(providerUser, productId) ||
    isProductVisible(providerUser, productId)
  );
}

function RuntimeAppBoundary({ children }) {
  const location = useLocation();
  const targetUrl = getCrossAppUrl(location.pathname, location.search);

  useEffect(() => {
    if (targetUrl) window.location.replace(targetUrl);
  }, [targetUrl]);

  if (targetUrl) {
    return <Typography sx={{ p: 2 }}>Opening the correct application...</Typography>;
  }
  return children;
}

const RouteGroups = ({ providerUser, setProviderUser, token, login, logout, setImage }) => {
  const careBrandHost = isCareBrandHost();
  const runtimeAppProfile = getRuntimeAppProfile();
  const dispatch = useDispatch();
  const location = useLocation();

  const assigningDefaultBusinessRef = useRef(false);
  const selectedBusinessForStaff = useSelectedBusiness();
  const businesses = useSelectBusinesses();
  const hasAnyBusiness = useMemo(
    () => Array.isArray(businesses) && businesses.some((b) => Number(b?.businessBasic?.id) > 0),
    [businesses]
  );
  const shouldBootstrapSessionData = useMemo(() => {
    const pathname = location.pathname;

    return (
      pathname.startsWith("/biz") ||
      pathname === "/profile" ||
      pathname.startsWith("/myschedule") ||
      pathname.startsWith("/care/dashboard") ||
      pathname.startsWith("/care/schedule") ||
      pathname.startsWith("/care/agent") ||
      pathname.startsWith("/care/admin") ||
      pathname.startsWith("/care/referrals") ||
      pathname.startsWith("/referrals/portal") ||
      pathname.startsWith("/referrer/portal")
    );
  }, [location.pathname]);





  const {
    loading: providerUserLoading,
    error: providerUserError,
    data: providerUserData,
    refetch: refetchProviderToken,
  } = useQuery(GET_TOKEN, {
    variables: {
      userId: providerUser?.userId ? Number(providerUser.userId) : null,
      businessId: providerUser?.defaultBusinessId
        ? Number(providerUser.defaultBusinessId)
        : null,
      staffId: null,
      subscriptionId: null,
    },
    skip: !shouldBootstrapSessionData || !providerUser?.userId,
  });
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);



  useEffect(() => {
    if (!providerUserLoading && !providerUserError && providerUserData) {
      if (providerUserData?.getToken?.token) {
        persistToken(providerUserData.getToken.token);
      }
      const decoded =
        providerUserData && providerUserData !== "undefined"
          ? jwtDecode(providerUserData.getToken.token)
          : null;
      setProviderUser(decoded);
    }
  }, [providerUserLoading, providerUserError, providerUserData, setProviderUser]);

  useEffect(() => {
    const hasUser = Number(providerUser?.userId || 0) > 0;
    const hasDefault = Number(providerUser?.defaultBusinessId || 0) > 0;
    const isAgentUser = isConciergeUser(providerUser);
    const fallbackId = Number(
      selectedBusinessForStaff?.businessBasic?.id || businesses?.[0]?.businessBasic?.id || 0
    );
    if (
      !hasUser ||
      hasDefault ||
      isAgentUser ||
      fallbackId <= 0 ||
      assigningDefaultBusinessRef.current
    ) {
      return;
    }

    assigningDefaultBusinessRef.current = true;
    (async () => {
      try {
        dispatch(setSelectedBusiness(fallbackId));
        await updateDefaultBusiness({
          variables: { defaultBusinessId: fallbackId },
        });
        const tokenResult = await refetchProviderToken({
          userId: Number(providerUser.userId),
          businessId: fallbackId,
          staffId: null,
          subscriptionId: null,
        });
        const nextToken = tokenResult?.data?.getToken?.token;
        if (nextToken) {
          persistToken(nextToken);
          setProviderUser(jwtDecode(nextToken));
        } else {
          setProviderUser((prev) => ({ ...prev, defaultBusinessId: fallbackId }));
        }
      } catch (error) {
        console.error("Failed to auto-assign default business:", error);
      } finally {
        assigningDefaultBusinessRef.current = false;
      }
    })();
  }, [
    providerUser?.userId,
    providerUser?.defaultBusinessId,
    selectedBusinessForStaff?.businessBasic?.id,
    businesses,
    dispatch,
    updateDefaultBusiness,
    refetchProviderToken,
    setProviderUser,
  ]);

  useEffect(() => {
    const hasSelectedBusiness = Number(selectedBusinessForStaff?.businessBasic?.id || 0) > 0;
    if (hasSelectedBusiness || !hasAnyBusiness) return;
    const fallbackId = Number(businesses?.[0]?.businessBasic?.id || 0);
    if (fallbackId > 0) {
      dispatch(setSelectedBusiness(fallbackId));
    }
  }, [dispatch, selectedBusinessForStaff?.businessBasic?.id, hasAnyBusiness, businesses]);

  useEffect(() => {
    const defaultBusinessId = Number(providerUser?.defaultBusinessId || 0);
    const selectedBusinessId = Number(selectedBusinessForStaff?.businessBasic?.id || 0);

    if (!shouldBootstrapSessionData || defaultBusinessId <= 0) return;
    if (selectedBusinessId === defaultBusinessId) return;

    const hasDefaultBusinessLoaded = businesses.some(
      (business) => Number(business?.businessBasic?.id || 0) === defaultBusinessId
    );

    if (hasDefaultBusinessLoaded) {
      dispatch(setSelectedBusiness(defaultBusinessId));
    }
  }, [
    providerUser?.defaultBusinessId,
    selectedBusinessForStaff?.businessBasic?.id,
    shouldBootstrapSessionData,
    businesses,
    dispatch,
  ]);



  const hasLoading = useMemo(
    () => providerUserLoading,
    [providerUserLoading]
  );
  const hasError = useMemo(
    () => providerUserError,
    [providerUserError]
  );
  const isCurrentPathEnabled = useMemo(
    () => isPathProductEnabled(location.pathname),
    [location.pathname]
  );
  const guestBizEntryRoute = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const productKey = params.get("v");

    if (productKey === "care") return "/care";
    if (productKey === "booking") return "/booking";

    return runtimeAppProfile.publicHomePath;
  }, [location.search, runtimeAppProfile.publicHomePath]);
  const shouldGateRouteLoading = useMemo(
    () => location.pathname.startsWith("/biz") || location.pathname === "/profile",
    [location.pathname]
  );

  // ---------- Guards ----------
  const ProtectedComponent = ({ children }) => {
    const location = useLocation();
    const isAgentUser = isConciergeUser(providerUser);
    if (!providerUser.userId) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    if (
      !providerUser?.defaultBusinessId &&
      !hasAnyBusiness &&
      !isAgentUser &&
      location.pathname !== "/biz/business"
    ) {
      if (!localStorage.getItem("redirectAfterBusiness")) {
        const loginRedirect = localStorage.getItem("redirectAfterLogin");
        const intended = loginRedirect || location.pathname + location.search;
        localStorage.setItem("redirectAfterBusiness", intended);
      }
      return <Navigate to="/biz/business" replace />;
    }
    return <>{children}</>;
  };

  const ProtectedComponentNeedToHaveBusiness = ({ children }) => {
    if (!token) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    if (!providerUser?.defaultBusinessId && !hasAnyBusiness && location.pathname !== "/biz/business") {
      return <Navigate to="/biz/business" replace />;
    }
    return children;
  };

  const RequireLoginOnly = ({ children }) => {
    const isAuthed = Boolean(token || providerUser?.userId);
    if (!isAuthed) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
  };

  const RequireStandaloneBookingAccess = ({ children }) => {
    if (!providerUser?.userId) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    const canUseBookingPortal =
      hasStandaloneBookingAccess(providerUser) ||
      hasWorkspaceProductAccess(providerUser, "booking");
    if (!canUseBookingPortal && providerUser?.defaultBusinessId) {
      return <Navigate to="/biz/pricing?product=booking" replace />;
    }
    return <>{children}</>;
  };

  const RequirePlatformAdmin = ({ children }) => {
    if (!providerUser.userId) {
      localStorage.setItem("redirectAfterLogin", location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
    if (providerUser?.isPlatformAdmin !== true) {
      return <Navigate to="/profile" replace />;
    }
    return <>{children}</>;
  };

  // Backward-compatible Google OAuth callback handler:
  // If Google redirects to /biz/staff with ?code=..., forward to the dedicated connect route.
  const StaffGoogleCallbackRedirect = ({ children }) => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const hasPendingAuth = Boolean(localStorage.getItem("calendarAuthPending"));

    if (code && hasPendingAuth) {
      return <Navigate to={`/connect/google_calendar?code=${encodeURIComponent(code)}`} replace />;
    }

    return children;
  };

  const NotFound = () => <Error />;

  const renderError = () => (
    <Typography variant="h6" color="error" align="center">
      An error occurred while loading the data. Please try again.
    </Typography>
  );

  const renderLoading = () => (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <h2>Loading...</h2>
      <p>Please wait while we fetch the data.</p>
      <div className="spinner" />
    </div>
  );

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  const postLoginRedirect = useMemo(
    () =>
      localStorage.getItem("redirectAfterLogin") ??
      (isConciergeUser(providerUser)
        ? "/care/agent"
        : hasAnyBusiness
            ? runtimeAppProfile.businessHomePath
          : "/biz/business"),
    [hasAnyBusiness, providerUser, runtimeAppProfile.businessHomePath]
  );

  // --------- LEGACY → CANONICAL REDIRECT HELPERS ---------
  // These keep old links working while consolidating on /b/:businessUrl

  const RESERVED_TOP = RESERVED_TOP_LEVEL_BUSINESS_SLUGS;

  const normalizeBusinessUrlParam = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";

    let pathish = raw;
    if (/^https?:\/\//i.test(pathish)) {
      try {
        pathish = new URL(pathish).pathname || "";
      } catch {
        // Keep raw value if URL parsing fails.
      }
    }

    const cleaned = pathish.replace(/^\/+|\/+$/g, "");
    if (!cleaned) return "";

    const parts = cleaned.split("/").filter(Boolean);
    if (!parts.length) return "";
    if (parts[0] === "b" && parts[1]) return parts[1];
    return parts[0];
  };

  function LegacyBusinessRootRedirect() {
    // /:businessUrl  ->  /b/:businessUrl
    const { businessUrl } = useParams();
    const loc = useLocation();
    if (!businessUrl || RESERVED_TOP.has(businessUrl.toLowerCase())) {
      return <NotFound />;
    }
    return <Navigate to={`/b/${businessUrl}${loc.search}`} replace />;
  }

  function LegacyPathRedirect({ build }) {
    // Generic mapper: given current params, build new path under /b
    const params = useParams();
    const loc = useLocation();
    const target = build(params);
    return <Navigate to={`${target}${loc.search}`} replace />;
  }

  const isAFHName = (name) => isCareCategoryName(name);
  const isAFHBusiness = (business, categoryName) => {
    return (
      isAFHName(categoryName) ||
      Boolean(business?.careSettings?.afhLicenseNumber) ||
      Boolean(business?.businessBasic?.careSettings?.afhLicenseNumber)
    );
  };
  const isClaimedBusiness = (business) => {
    const raw = String(business?.claimStatus || "").trim().toUpperCase();
    const hasClaimer =
      business?.createdBy !== null && business?.createdBy !== undefined;
    return hasClaimer || ["CLAIMED", "VERIFIED", "APPROVED", "OWNED"].includes(raw);
  };

  const cleanRecordText = (value) => String(value || "").trim();

  const getStateReportsUrl = (business) => {
    const links = Array.isArray(business?.links) ? business.links : [];
    return cleanRecordText(
      links.find((link) => cleanRecordText(link?.platform).toLowerCase() === "state_reports")?.url ||
        links.find((link) => cleanRecordText(link?.platform).toLowerCase().includes("report"))?.url
    );
  };

  const getRecordAddress = (business) => {
    const listedAddress = cleanRecordText(business?.address);
    if (listedAddress) return listedAddress;
    return [business?.street, business?.cityName, business?.stateCode, business?.zipcode]
      .map(cleanRecordText)
      .filter(Boolean)
      .join(", ");
  };

  const getRecordPhone = (business) => {
    const phone = (business?.phoneNumbers || []).find((row) => cleanRecordText(row?.phoneNumber))?.phoneNumber;
    return cleanRecordText(phone);
  };

  const splitRecordLines = (value) =>
    cleanRecordText(value)
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);

  const getPublicCareStatusLabel = (business) => {
    const licenseStatus = cleanRecordText(business?.careSettings?.licenseStatus).toUpperCase();
    if (licenseStatus) {
      if (["ACTIVE", "OPEN", "OP", "EXISTING"].includes(licenseStatus)) return "Active";
      if (["INACTIVE", "CLOSED", "CL", "REVOKED", "SUSPENDED", "TERMINATED", "EXPIRED"].includes(licenseStatus)) {
        return "Inactive";
      }
      return licenseStatus
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }
    return cleanRecordText(business?.status).toUpperCase() === "INACTIVE" ? "Inactive" : "Active";
  };

  const getRecordCategory = (business, categoryName) => {
    return (
      cleanRecordText(business?.businessSubcategory?.name) ||
      cleanRecordText(categoryName) ||
      cleanRecordText(business?.businessTopCategory?.name) ||
      "AF"
    );
  };

  function PublicRecordFact({ label, value, icon, children }) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {icon}
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#314052", textTransform: "uppercase" }}>
            {label}
          </Typography>
        </Stack>
        {children || (
          <Typography sx={{ fontSize: 15, color: "#111827", lineHeight: 1.65 }}>
            {value || "Not listed"}
          </Typography>
        )}
      </Box>
    );
  }

  function PublicCareRecordDetailPage({ business, categoryName }) {
    const [claimDialogOpen, setClaimDialogOpen] = useState(false);
    const navigate = useNavigate();
    const name = business?.name || "Care business";
    const businessId = Number(business?.id || 0);
    const category = getRecordCategory(business, categoryName);
    const cityState = [business?.cityName, business?.stateCode].filter(Boolean).join(", ");
    const address = getRecordAddress(business);
    const phone = getRecordPhone(business);
    const phoneHref = phone ? `tel:${phone.replace(/[^\d+]/g, "")}` : "";
    const publicBookingHref = business?.bookingPageUrl || "";
    const hasPublicBooking = Boolean(publicBookingHref);
    const directionsHref = address
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
      : "";
    const license = cleanRecordText(business?.careSettings?.afhLicenseNumber) || "Not listed";
    const careSettings = business?.careSettings || {};
    const careMeta = business?.careMeta || {};
    const specialty = cleanRecordText(business?.careSettings?.specialty) || "Not listed";
    const stateReportsUrl = getStateReportsUrl(business);
    const bedCount = Number(business?.careMeta?.bedsTotal ?? 0);
    const openBedsCount = Number(business?.careMeta?.bedsOpen ?? 0);
    const hasOpenings = openBedsCount > 0;
    const statusLabel = getPublicCareStatusLabel(business);
    const isActiveStatus = statusLabel === "Active";
    const canClaimBusiness = !isClaimedBusiness(business);
    const disclaimerText = canClaimBusiness
      ? "This information comes from publicly available licensing records and is provided for general reference only. Please confirm details directly with the facility or the appropriate licensing authority."
      : "Some information may be updated by the owner or authorized team. This data is provided as-is for informational purposes only, and we do not guarantee accuracy or current availability. Please verify details directly with the facility.";
    const careLevels = Array.isArray(careSettings?.careLevels) ? careSettings.careLevels : [];
    const images = Array.isArray(business?.images)
      ? business.images.map((image) => image?.url).filter(Boolean)
      : [];
    const paymentItems = [
      careSettings?.acceptsMedicaid ? "Medicaid accepted" : null,
      careSettings?.acceptsVeteransBenefits ? "Veterans benefits accepted" : null,
      careSettings?.acceptsLongTermCareInsurance ? "Long-term care insurance accepted" : null,
      careSettings?.basePriceMonthly ? `Base monthly price: $${Number(careSettings.basePriceMonthly).toLocaleString()}` : null,
    ].filter(Boolean);
    const languages = splitRecordLines(careSettings?.languagesSpoken);
    const featureItems = [
      careSettings?.hasPrivateRooms ? "Private rooms" : null,
      careSettings?.hasSharedRooms ? "Shared rooms" : null,
      careSettings?.privateBathroomAvailable ? "Private bathroom available" : null,
      careSettings?.hoyerLiftAvailable ? "Hoyer lift available" : null,
      careSettings?.has24HourCare ? "24-hour care" : null,
      careSettings?.hasRegisteredNurse ? "Registered nurse" : null,
      careSettings?.hasVisitingPhysician ? "Visiting physician" : null,
      careSettings?.acceptsWheelchairAccess ? "Wheelchair access" : null,
      careSettings?.acceptsBedboundResidents ? "Accepts bedbound residents" : null,
      careSettings?.acceptsDevelopmentalDisabilities ? "Developmental disabilities support" : null,
      careSettings?.isPetFriendly ? "Pet friendly" : null,
      careSettings?.genderPreference ? `Gender preference: ${careSettings.genderPreference}` : null,
      languages.length ? `Languages: ${languages.join(", ")}` : null,
    ].filter(Boolean);
    const detailSections = [
      { title: "Home Highlights", value: careSettings?.specialFeaturesDescription || careMeta?.notes },
      { title: "Care Approach", value: careSettings?.philosophyValuesDescription },
      { title: "Daily Life", value: careSettings?.dailyLifeDescription },
      { title: "Included Services", value: careSettings?.includedServicesDetails },
      { title: "Meals", value: careSettings?.mealsDetails },
      { title: "Transportation", value: careSettings?.transportationDetails },
    ].filter((section) => cleanRecordText(section.value));
    const { data: jobsData } = useQuery(GET_JOB_POSTINGS_BY_BUSINESS, {
      variables: { businessId, onlyActive: true, limit: 3, offset: 0 },
      skip: !businessId,
    });
    const openJobs = Array.isArray(jobsData?.jobPostingsByBusiness)
      ? jobsData.jobPostingsByBusiness.filter((job) => job?.isActive !== false)
      : [];

    const handleShare = async () => {
      const shareUrl = typeof window !== "undefined" ? window.location.href : "";
      try {
        if (navigator?.share) {
          await navigator.share({ title: name, url: shareUrl });
        } else if (navigator?.clipboard && shareUrl) {
          await navigator.clipboard.writeText(shareUrl);
        }
      } catch { }
    };

    return (
      <Box sx={{ bgcolor: "#fff", minHeight: "100dvh", px: { xs: 2, md: 3 }, py: 1.5 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/care/find")}
          sx={{ textTransform: "none", color: "#25324a", mb: 2 }}
        >
          Back to Search
        </Button>

        <Box
          sx={{
            bgcolor: "#f3f4f6",
            color: "#111827",
            border: "1px solid",
            borderColor: "#e5e7eb",
            borderRadius: 2,
            px: { xs: 2, md: 4 },
            py: { xs: 2.5, md: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, fontSize: { xs: 24, md: 30 }, lineHeight: 1.15 }}>
              {name}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 14, color: "#4b5563" }}>
              {[category, cityState].filter(Boolean).join(" - ")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<ShareOutlinedIcon />}
              onClick={handleShare}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#e5e7eb", color: "#111827", boxShadow: "none", fontWeight: 700, "&:hover": { bgcolor: "#d1d5db" } }}
            >
              Share
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(`/${business?.url}/care/referral`, {
                state: {
                  businessId,
                  business,
                  defaultType: "availability",
                  source: "publicCareRecordDetailPage",
                },
              })}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#111827", color: "white", boxShadow: "none", fontWeight: 700, "&:hover": { bgcolor: "#1f2937" } }}
            >
              Check Availability
            </Button>
            {canClaimBusiness ? (
              <Button
                variant="contained"
                onClick={() => setClaimDialogOpen(true)}
                sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#ffffff", color: "#111827", border: "1px solid #d1d5db", boxShadow: "none", fontWeight: 700, "&:hover": { bgcolor: "#f9fafb" } }}
              >
                Update your care listing
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Stack direction="row" spacing={2.25} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", py: 2 }}>
          <Chip
            label={statusLabel}
            sx={{
              ml: { xs: 0, md: "auto" },
              bgcolor: isActiveStatus ? "#111827" : "#e5e7eb",
              color: isActiveStatus ? "white" : "#374151",
              fontWeight: 800,
              borderRadius: 999,
            }}
          />
        </Stack>

        {images.length ? (
          <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.25, md: 1.5 }, mb: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: images.length > 1 ? "minmax(0, 2fr) minmax(260px, 1fr)" : "1fr" },
                gridAutoRows: { xs: "auto", md: images.length > 1 ? "minmax(0, 1fr)" : "auto" },
                gap: 1.25,
                alignItems: "stretch",
                height: { xs: "auto", md: images.length > 1 ? 420 : "auto" },
              }}
            >
              <Box
                component="img"
                src={images[0]}
                alt={`${name} photo 1`}
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: "auto", md: images.length > 1 ? "100%" : "auto" },
                  aspectRatio: { xs: "4 / 3", md: images.length > 1 ? "auto" : "16 / 7" },
                  objectFit: "cover",
                  display: "block",
                  borderRadius: 1.5,
                }}
              />
              {images.length > 1 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                    height: { xs: "auto", md: "100%" },
                  }}
                >
                  {images.slice(1, 5).map((url, index) => (
                    <Box
                      key={`${url}-${index}`}
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: { xs: "auto", md: "100%" },
                        minHeight: 0,
                        aspectRatio: { xs: "1 / 1", md: "auto" },
                        overflow: "hidden",
                        borderRadius: 1.5,
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt={`${name} photo ${index + 2}`}
                        loading="lazy"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {index === 3 && images.length > 5 ? (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "rgba(17, 24, 39, 0.58)",
                            color: "#fff",
                            fontSize: { xs: 18, md: 22 },
                            fontWeight: 900,
                            letterSpacing: 0,
                          }}
                        >
                          +{images.length - 5} photos
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          </Paper>
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.25, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                  <ArticleOutlinedIcon sx={{ color: "#4338ca" }} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    License Information
                  </Typography>
                </Stack>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <PublicRecordFact label="License Number" value={license} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PublicRecordFact
                      label="Licensed Bed Count"
                      value={bedCount > 0 ? `${bedCount} Beds` : "Not listed"}
                      icon={<HotelOutlinedIcon sx={{ fontSize: 18, color: "#4b5563" }} />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PublicRecordFact label="Facility Type" value={category} />
                  </Grid>
                  {stateReportsUrl ? (
                    <Grid item xs={12} sm={6}>
                      <PublicRecordFact
                        label="State Reports"
                        icon={<ArticleOutlinedIcon sx={{ fontSize: 18, color: "#4b5563" }} />}
                      >
                        <Button
                          component="a"
                          href={stateReportsUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ p: 0, minWidth: 0, textTransform: "none", textAlign: "left", justifyContent: "flex-start", color: "#111827" }}
                        >
                          Open state reports
                        </Button>
                      </PublicRecordFact>
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.25, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <MedicalServicesOutlinedIcon sx={{ color: "#4338ca" }} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Specialty
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 16, color: "#1f2937", lineHeight: 1.65 }}>
                  {specialty}
                </Typography>
              </Paper>

              {careLevels.length || paymentItems.length || featureItems.length ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.25, md: 3 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                    Care Details
                  </Typography>
                  <Stack spacing={2}>
                    {careLevels.length ? (
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                          Care Levels
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                          {careLevels.map((level) => (
                            <Chip key={level?.id || level?.name} label={level?.name || String(level)} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                    {paymentItems.length ? (
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                          Payment
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                          {paymentItems.map((item) => (
                            <Chip key={item} label={item} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                    {featureItems.length ? (
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", mb: 1 }}>
                          Features
                        </Typography>
                        <Grid container spacing={1}>
                          {featureItems.map((item) => (
                            <Grid key={item} item xs={12} sm={6}>
                              <Typography sx={{ fontSize: 14, color: "#334155" }}>
                                {item}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}

              {detailSections.map((section) => (
                <Paper key={section.title} variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.25, md: 3 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    {section.title}
                  </Typography>
                  <Typography sx={{ fontSize: 15, color: "#334155", lineHeight: 1.75, whiteSpace: "pre-line" }}>
                    {section.value}
                  </Typography>
                </Paper>
              ))}

              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, bgcolor: "#f8fafc" }}>
                <Typography sx={{ fontSize: 12, color: "#334155", lineHeight: 1.7 }}>
                  <strong>Disclaimer:</strong> {disclaimerText}
                </Typography>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.25, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>
                  Contact & Location
                </Typography>
                <Stack spacing={3}>
                  <PublicRecordFact
                    label="Address"
                    icon={<LocationOnOutlinedIcon sx={{ fontSize: 18, color: "#4b5563" }} />}
                  >
                    {address ? (
                      <Button
                        component="a"
                        href={directionsHref}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ p: 0, minWidth: 0, textTransform: "none", textAlign: "left", justifyContent: "flex-start", color: "#111827", lineHeight: 1.65 }}
                      >
                        {address}
                      </Button>
                    ) : (
                      <Typography sx={{ fontSize: 15 }}>Not listed</Typography>
                    )}
                  </PublicRecordFact>
                  <Divider />
                  <PublicRecordFact
                    label="Phone"
                    icon={<PhoneOutlinedIcon sx={{ fontSize: 18, color: "#4b5563" }} />}
                  >
                    {phone ? (
                      <Button component="a" href={phoneHref} sx={{ p: 0, minWidth: 0, textTransform: "none", color: "#111827" }}>
                        {phone}
                      </Button>
                    ) : (
                      <Typography sx={{ fontSize: 15 }}>Not listed</Typography>
                    )}
                  </PublicRecordFact>
                  {(hasOpenings || hasPublicBooking || openJobs.length) ? (
                    <>
                      <Divider />
                      <Box>
                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                          {hasOpenings ? (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<HotelOutlinedIcon />}
                              onClick={() => navigate(`/${business?.url}/care/referral`, {
                                state: {
                                  businessId,
                                  business,
                                defaultType: "availability",
                                source: "publicCareRecordDetailPageActions",
                              },
                            })}
                              sx={{
                                textTransform: "none",
                                borderRadius: 999,
                                bgcolor: "#0f7a46",
                                boxShadow: "none",
                                minHeight: 30,
                                px: 1.4,
                                fontWeight: 800,
                                fontSize: 12,
                                "&:hover": { bgcolor: "#0b6338" },
                              }}
                            >
                              {openBedsCount} {openBedsCount === 1 ? "opening" : "openings"}
                            </Button>
                          ) : null}
                          {hasPublicBooking ? (
                            <Button
                              component="a"
                              href={publicBookingHref}
                              variant="outlined"
                              size="small"
                              startIcon={<EventIcon />}
                              sx={{
                                textTransform: "none",
                                borderRadius: 999,
                                minHeight: 30,
                                px: 1.4,
                                fontWeight: 800,
                                fontSize: 12,
                              }}
                            >
                              Book Online
                            </Button>
                          ) : null}
                          {openJobs.length ? (
                            <Button
                              component="a"
                              href={`/b/${business?.url}/jobs`}
                              variant="outlined"
                              size="small"
                              startIcon={<WorkOutlineIcon />}
                              sx={{
                                textTransform: "none",
                                borderRadius: 999,
                                minHeight: 30,
                                px: 1.4,
                                fontWeight: 800,
                                fontSize: 12,
                                bgcolor: "#fff7e6",
                                borderColor: "#f3d18a",
                                color: "#8a5a00",
                                "&:hover": { bgcolor: "#fff0cc", borderColor: "#e5bd67" },
                              }}
                            >
                              {openJobs.length === 1 ? "1 job" : `${openJobs.length} jobs`}
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>
                    </>
                  ) : null}
                </Stack>
              </Paper>

              {openJobs.length ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.25, md: 3 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                    Job Openings
                  </Typography>
                  <Stack spacing={1.25}>
                    {openJobs.map((job) => (
                      <Box key={job?.id} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
                        <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                          {job?.title || "Care job"}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.25 }}>
                          {[job?.employmentType, job?.location].filter(Boolean).join(" - ") || "Open role"}
                        </Typography>
                        <Button
                          size="small"
                          href={`/b/${business?.url}/jobs`}
                          sx={{ textTransform: "none", fontWeight: 700, mt: 0.75, p: 0, minWidth: 0 }}
                        >
                          View jobs
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          </Grid>
        </Grid>

        {canClaimBusiness ? (
          <RegisterBusinessDialog
            open={claimDialogOpen}
            onClose={() => setClaimDialogOpen(false)}
            source="public-unclaimed-business"
            verticalId="care"
            title="Update your care listing"
            expectedBusinessId={Number(business?.id || 0)}
          />
        ) : null}
      </Box>
    );
  }

  function PublicClaimAction({ business, verticalId = "care" }) {
    const [claimDialogOpen, setClaimDialogOpen] = useState(false);

    return (
      <>
        <Box
          sx={{
            position: "fixed",
            top: { xs: 72, sm: 88 },
            right: { xs: 12, sm: 24 },
            zIndex: (theme) => theme.zIndex.appBar + 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => setClaimDialogOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 999,
              boxShadow: "0 10px 24px rgba(15, 23, 42, 0.18)",
            }}
          >
            Claim this business
          </Button>
        </Box>

        <RegisterBusinessDialog
          open={claimDialogOpen}
          onClose={() => setClaimDialogOpen(false)}
          source="public-unclaimed-business"
          verticalId={verticalId}
          title="Claim this business"
          expectedBusinessId={Number(business?.id || 0)}
        />
      </>
    );
  }

  function PublicCareBusinessDetailPage({ business, categoryName }) {
    return <PublicCareRecordDetailPage business={business} categoryName={categoryName} />;
  }

  function PublicBusinessPageRouter() {
    const { businessUrl } = useParams();
    const normalizedBusinessUrl = normalizeBusinessUrlParam(businessUrl);
    const { data: businessData, loading: businessLoading } = useQuery(GET_BUSINESS_BY_URL, {
      variables: { businessUrl: normalizedBusinessUrl },
      skip: !normalizedBusinessUrl,
    });
    const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);

    if (businessLoading) return <Typography sx={{ p: 2 }}>Loading...</Typography>;
    const business = businessData?.businessByUrl;
    if (!business) return <NotFound />;
    const category = (categoriesData?.getBusinessCategories || []).find(
      (c) => Number(c?.id) === Number(business?.businessCategoryId)
    );
    const isAFH = isAFHBusiness(business, category?.name);
    if (isAFH) {
      return <PublicCareBusinessDetailPage business={business} categoryName={category?.name} />;
    }

    return (
      <>
        {!isClaimedBusiness(business) ? <PublicClaimAction business={business} verticalId="booking" /> : null}
        <ExternalTimelyRoute />
      </>
    );
  }

  function PublicCarePageRouter() {
    const { businessUrl } = useParams();
    const normalizedBusinessUrl = normalizeBusinessUrlParam(businessUrl);
    const { data: businessData, loading: businessLoading } = useQuery(GET_BUSINESS_BY_URL, {
      variables: { businessUrl: normalizedBusinessUrl },
      skip: !normalizedBusinessUrl,
    });
    const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);

    if (businessLoading) return <Typography sx={{ p: 2 }}>Loading...</Typography>;
    const business = businessData?.businessByUrl;
    if (!business) return <NotFound />;
    const category = (categoriesData?.getBusinessCategories || []).find(
      (c) => Number(c?.id) === Number(business?.businessCategoryId)
    );
    const isAFH = isAFHBusiness(business, category?.name);
    return isAFH ? (
      <PublicCareBusinessDetailPage business={business} categoryName={category?.name} />
    ) : (
      <Navigate to={`/b/${normalizedBusinessUrl}`} replace />
    );
  }

  function BusinessDashboardRouter() {
    const dispatch = useDispatch();
    const location = useLocation();
    const selectedBusiness = useSelectedBusiness();
    const businesses = useSelectBusinesses();
    const hasBusinessSelection = Boolean(selectedBusiness?.businessBasic?.id);
    const fallbackBusinessId = Number(
      providerUser?.defaultBusinessId || businesses?.[0]?.businessBasic?.id || 0
    );
    const {
      loading: myBusinessesLoading,
      error: myBusinessesError,
    } = useLoadReduxStoreData(BusinessQueryNames.GET_MY_BUSINESSES, {
      enabled: Boolean(providerUser?.userId),
    });
    const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
    const selectedCategory = (categoriesData?.getBusinessCategories || []).find(
      (c) => Number(c?.id) === Number(selectedBusiness?.businessBasic?.businessCategoryId)
    );
    const isAFH = isAFHBusiness(selectedBusiness, selectedCategory?.name);
    const isCareEligible = isCareBusinessEligible(selectedCategory, selectedBusiness);
    const canUseCareWorkspace = hasLocalWorkspaceOverride(providerUser, "care")
      ? hasLocalWorkspaceProduct(providerUser, "care")
      : (
          hasCareWorkspaceAccess(providerUser, isAFH) ||
          isProductVisible(providerUser, "care")
        );
    const canUseStandaloneBooking = hasStandaloneBookingAccess(providerUser, isAFH);
    const hasBookingWorkspaceAccess = hasWorkspaceProductAccess(providerUser, "booking");
    const canUseBookingPortal = canUseStandaloneBooking || hasBookingWorkspaceAccess;
    const activeProductId = (() => {
      const params = new URLSearchParams(location.search);
      const verticalFromQuery = params.get("v");
      if (verticalFromQuery && isVerticalId(verticalFromQuery)) {
        return verticalFromQuery;
      }

      const detected = detectVerticalFromPath(location.pathname);
      if (detected) return detected;

      const stored = getStoredVertical();
      if (stored && isVerticalId(stored)) {
        return stored;
      }

      return canUseCareWorkspace ? "care" : "booking";
    })();
    const shouldShowCareDashboard =
      activeProductId === "care" && canUseCareWorkspace;
    const shouldShowBookingDashboard =
      activeProductId === "booking" && canUseBookingPortal;

    useEffect(() => {
      if (hasBusinessSelection || !fallbackBusinessId) return;
      dispatch(setSelectedBusiness(fallbackBusinessId));
    }, [dispatch, hasBusinessSelection, fallbackBusinessId]);

    if (myBusinessesLoading && !hasBusinessSelection) {
      return <Typography sx={{ p: 2 }}>Loading dashboard...</Typography>;
    }
    if (!hasBusinessSelection && myBusinessesError) {
      return <Typography sx={{ p: 2 }}>Unable to load dashboard. Please refresh.</Typography>;
    }
    if (!hasBusinessSelection && !myBusinessesLoading) {
      return <Navigate to="/biz/business" replace />;
    }

    return shouldShowCareDashboard ? (
      isCareEligible ? (
        <RequireProviderAgreement businessId={providerUser.businessId}>
          <CareDashboardProductPage />
        </RequireProviderAgreement>
      ) : (
        <CareSetupRequiredPage />
      )
    ) : shouldShowBookingDashboard ? (
      <Navigate to="/biz/bookingtypes?v=booking" replace />
    ) : canUseCareWorkspace ? (
      isCareEligible ? (
        <RequireProviderAgreement businessId={providerUser.businessId}>
          <CareDashboardProductPage />
        </RequireProviderAgreement>
      ) : (
        <CareSetupRequiredPage />
      )
    ) : (
      <Navigate to="/biz/bookingtypes?v=booking" replace />
    );
  }

  function AFHResidentRouteGuard({ children }) {
    const selectedBusiness = useSelectedBusiness();
    const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
    const selectedCategory = (categoriesData?.getBusinessCategories || []).find(
      (c) =>
        Number(c?.id) ===
        Number(
          selectedBusiness?.businessBasic?.businessCategoryId ??
          selectedBusiness?.businessCategoryId
        )
    );
    const isAFH = isAFHBusiness(selectedBusiness, selectedCategory?.name);
    const isCareEligible = isCareBusinessEligible(selectedCategory, selectedBusiness);

    if (!selectedBusiness?.businessBasic?.id) {
      return <Typography sx={{ p: 2 }}>Loading...</Typography>;
    }

    return isAFH && isCareEligible ? (
      children
    ) : (
      <Navigate to="/biz/dashboard?v=care" replace />
    );
  }

  // ---------- Routes ----------
  const routes = useMemo(
    () => (
      <Routes>
        {/* Root → smart redirect */}
        <Route path="/" element={<RootRedirect providerUser={providerUser} />} />

        {/* Public landing & chooser */}
        <Route
          path="/choose"
          element={<Navigate to={getRuntimePublicHomePath()} replace />}
        />
        <Route path="/business/register" element={<BusinessRegisterPublicPage />} />
        <Route
          path="/care-directory/register"
          element={<LegacyPathRedirect build={() => "/business/register"} />}
        />
        <Route
          path="/care/register"
          element={<LegacyPathRedirect build={() => "/business/register"} />}
        />

        {/* Vertical directories (paths stay free & clean) */}
        <Route
          path="/care-directory"
          element={<LegacyPathRedirect build={() => "/care/find"} />}
        />
        <Route path="/resident-management" element={<ResidentManagementPublicLandingPage />} />
        <Route
          path="/emar"
          element={<LegacyPathRedirect build={() => "/resident-management"} />}
        />
        <Route
          path="/care"
          element={
            careBrandHost
              ? <Navigate to={`/find${location.search || ""}${location.hash || ""}`} replace />
              : <LegacyPathRedirect build={() => "/care/find"} />
          }
        />
        <Route
          path="/care/find"
          element={careBrandHost ? <Navigate to={`/find${location.search || ""}${location.hash || ""}`} replace /> : <CareBrowsePage />}
        />
        <Route
          path="/find"
          element={careBrandHost ? <CareBrowsePage /> : <LegacyPathRedirect build={() => "/care/find"} />}
        />
        <Route
          path="/care/refer"
          element={<LegacyPathRedirect build={() => "/referrals"} />}
        />
        <Route path="/referrals/start" element={<CareFindPage />} />
        <Route path="/referrer/start" element={<LegacyPathRedirect build={() => "/referrals/start"} />} />
        <Route
          path="/care/dashboard"
          element={
            <ProtectedComponent>
              <RequireProviderAgreement businessId={providerUser.businessId}>
                <CareDashboardProductPage />
              </RequireProviderAgreement>
            </ProtectedComponent>
          }
        />
        <Route
          path="/care/schedule"
          element={
            <ProtectedComponent>
              <RequireProviderAgreement businessId={providerUser.businessId}>
                <CareSchedulePortal />
              </RequireProviderAgreement>
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/resident"
          element={
            <ProtectedComponent>
              <AFHResidentRouteGuard>
                <ResidentPortal />
              </AFHResidentRouteGuard>
            </ProtectedComponent>
          }
        />
        <Route
          path="/care/city/:cityId"
          element={careBrandHost ? <Navigate to={`${location.pathname.replace(/^\/care/, "") || "/"}${location.search || ""}${location.hash || ""}`} replace /> : <SearchPage />}
        />
        <Route
          path="/care/city/:cityId/:citySlug"
          element={careBrandHost ? <Navigate to={`${location.pathname.replace(/^\/care/, "") || "/"}${location.search || ""}${location.hash || ""}`} replace /> : <SearchPage />}
        />
        <Route
          path="/care/state/:stateId"
          element={careBrandHost ? <Navigate to={`${location.pathname.replace(/^\/care/, "") || "/"}${location.search || ""}${location.hash || ""}`} replace /> : <SearchPage />}
        />
        <Route
          path="/care/state/:stateId/:stateSlug"
          element={careBrandHost ? <Navigate to={`${location.pathname.replace(/^\/care/, "") || "/"}${location.search || ""}${location.hash || ""}`} replace /> : <SearchPage />}
        />
        <Route
          path="/city/:cityId"
          element={careBrandHost ? <SearchPage /> : <LegacyPathRedirect build={({ cityId }) => `/care/city/${cityId}`} />}
        />
        <Route
          path="/city/:cityId/:citySlug"
          element={careBrandHost ? <SearchPage /> : <LegacyPathRedirect build={({ cityId, citySlug }) => `/care/city/${cityId}/${citySlug}`} />}
        />
        <Route
          path="/state/:stateId"
          element={careBrandHost ? <SearchPage /> : <LegacyPathRedirect build={({ stateId }) => `/care/state/${stateId}`} />}
        />
        <Route
          path="/state/:stateId/:stateSlug"
          element={careBrandHost ? <SearchPage /> : <LegacyPathRedirect build={({ stateId, stateSlug }) => `/care/state/${stateId}/${stateSlug}`} />}
        />

        <Route path="/rentals" element={<VerticalLanding id="rentals" />} />
        <Route path="/agent" element={<VerticalLanding id="agent" />} />
        <Route path="/talent" element={<VerticalLanding id="talent" />} />
        <Route path="/booking" element={<ExternalTimelyRoute />} />
        <Route path="/booking/start" element={<ExternalTimelyRoute />} />
        <Route path="/booking/category/:category" element={<ExternalTimelyRoute />} />
        <Route path="/connect/agentcrafty" element={<ExternalTimelyRoute />} />
        <Route path="/b/connect/agentcrafty" element={<ExternalTimelyRoute />} />
        <Route path="/connect/telegram" element={<ExternalTimelyRoute />} />
        <Route path="/capture" element={<ExternalTimelyRoute />} />
        <Route path="/i/:token" element={<ExternalTimelyRoute />} />
        <Route
          path="/booking/dashboard"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/care/referrals/group/:groupId"
          element={
            <RequireLoginOnly>
              <BroadcastGroupPage />
            </RequireLoginOnly>
          }
        />
        <Route
          path="/referrals"
          element={<ReferrerLandingPage />}
        />
        <Route
          path="/referrals/portal"
          element={
            <RequireLoginOnly>
              <ReferrerPortalPage />
            </RequireLoginOnly>
          }
        />
        {/* Optional path-style variant so links like /referrals/portal/:token also work */}
        <Route
          path="/referrals/portal/:token"
          element={
            <RequireLoginOnly>
              <ReferrerPortalPage />
            </RequireLoginOnly>
          }
        />
        <Route path="/referrer" element={<LegacyPathRedirect build={() => "/referrals"} />} />
        <Route path="/referrer/portal" element={<LegacyPathRedirect build={() => "/referrals/portal"} />} />
        <Route path="/referrer/portal/:token" element={<LegacyPathRedirect build={({ token }) => `/referrals/portal/${token}`} />} />

        {/* Work flow */}
        {/* <Route path="/provider/:businessId/referrals" element={<ProviderDashboard />} /> */}

        <Route path="/b/:businessUrl/care" element={<PublicCarePageRouter />} />
        <Route
          path="/care/agreement"
          element={
            <RequireLoginOnly>
              <ProviderAgreementPage />
            </RequireLoginOnly>
          }
        />

        {/* To send quick referral to provider*/}
        <Route
          path="/b/:businessUrl/care/referral"
          element={
            <RequireLoginOnly>
              <RequestOrReferralPage />
            </RequireLoginOnly>
          }
        />
        <Route
          path="/care/request"
          element={
            <RequireLoginOnly>
              <RequestOrReferralPage />
            </RequireLoginOnly>
          }
        />
        <Route path="/care/agent/apply" element={<AgentApplicationPage />} />
        <Route path="/care/agent" element={
          <ProtectedComponent><ConciergeCityBoardPage /></ProtectedComponent>
        } />
        <Route
          path="/care/agent/agreement"
          element={
            <ProtectedComponent>
              <AgentCompAgreementPage />
            </ProtectedComponent>
          }
        />
        <Route
          path="/care/agent/admin"
          element={
            <ProtectedComponent>
              <ConciergeAdminDashboardPage />
            </ProtectedComponent>
          }
        />
        <Route
          path="/care/admin"
          element={
            <RequirePlatformAdmin>
              <PlanyAdminDashboardPage />
            </RequirePlatformAdmin>
          }
        />

        <Route path="/care/claim-resume" element={<ClaimResume />} />
        <Route path="/marketplace" element={<Navigate to="/care/marketplace" replace />} />
        <Route path="/care/marketplace" element={<MarketplacePage />} />
        <Route path="/care/marketplace/new" element={<MarketplaceCreatePage />} />
        <Route path="/b/marketplace" element={<Navigate to="/care/marketplace" replace />} />
        {/* Care dashboard */}
        <Route
          path="/biz/dashboard"
          element={
            <PayPalScriptProvider options={{ "client-id": clientId }}>
              <BusinessDashboardRouter />
            </PayPalScriptProvider>
          }
        />

        <Route path="/jobs" element={<Navigate to="/care/jobs" replace />} />
        <Route path="/care/jobs/find" element={<JobSearchLandingPage />} />
        <Route path="/care/jobs/apply" element={<LegacyPathRedirect build={() => "/care/jobs/find"} />} />
        <Route path="/care/jobs/pricing" element={<CareJobPricingPage />} />
        <Route path="/care/jobs/post" element={<JobPostLandingPage />} />
        <Route path="care/jobs" element={<JobBoardPage />} />
        <Route path="/b/jobs" element={<Navigate to="/care/jobs" replace />} />
        <Route path="/b/:businessUrl/jobs" element={<JobBoardPage />} />
        <Route path="/b/:businessUrl/jobs/:jobId" element={<JobDetailPage />} />
        <Route
          path="/b/:businessUrl/manage/jobs"
          element={
            <ProtectedComponent>
              <Navigate to="/biz/manage/jobs" replace />
            </ProtectedComponent>
          }
        />

        {/* ---------- CANONICAL BUSINESS URLs (all verticals) ---------- */}
        {/* Business root (was "/:businessUrl") */}
        <Route path="/b/:businessUrl" element={<PublicBusinessPageRouter />} />

        {/* Booking public root and booking type public page */}
        <Route path="/b/:businessUrl/booking" element={<ExternalTimelyRoute />} />
        <Route path="/b/:businessUrl/booking/:bookingTypeLink" element={<ExternalTimelyRoute />} />

        {/* Staff user page & wallet/tip */}
        <Route
          path="/b/:businessUrl/:staffUrl"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/b/:businessUrl/:staffUrl/wallet"
          element={
            <ProtectedComponent>
              <TipProvider>
                <WalletDashboard />
              </TipProvider>
            </ProtectedComponent>
          }
        />
        <Route
          path="/b/wallet"
          element={
            <ProtectedComponent>
              <TipProvider>
                <WalletDashboard />
              </TipProvider>
            </ProtectedComponent>
          }
        />
        <Route
          path="/b/:businessUrl/:staffUrl/tip"
          element={
            <ProtectedComponent>
              <TipProvider>
                <TipPage />
              </TipProvider>
            </ProtectedComponent>
          }
        />
        <Route
          path="/b/:businessUrl/tip"
          element={
            <ProtectedComponent>
              <TipProvider>
                <TipPage />
              </TipProvider>
            </ProtectedComponent>
          }
        />

        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancel" element={<BillingCancel />} />

        {/* Time selection & confirm */}
        <Route
          path="/b/:businessUrl/:staffUrl/:bookingTypeLink/:time"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/b/:businessUrl/booking/servicesConfirmed/:title/:timeRange/:time/:color/:staffUrl/:userName"
          element={<ExternalTimelyRoute />}
        />

        {/* Hosted & embeddable booking */}
        {/* Canonical embed */}
        <Route
          path="/book/embed/:businessUrl"
          element={<ExternalTimelyRoute />}
        />
        {/* (Optional) hosted booking page, if you want a non-embed view */}
        {/* <Route path="/book/b/:businessUrl" element={<DirectBooking />} /> */}

        {/* ---------- LEGACY ROUTES → REDIRECTS (keep old links alive) ---------- */}
        {/* old embed route: /embed/:businessUrl -> /book/embed/:businessUrl */}
        <Route
          path="/embed/:businessUrl"
          element={<LegacyPathRedirect build={({ businessUrl }) => `/book/embed/${businessUrl}`} />}
        />

        {/* legacy slug-first business root: "/:businessUrl" */}
        <Route path="/:businessUrl" element={<LegacyBusinessRootRedirect />} />

        {/* legacy care-referral under slug-first */}
        <Route
          path="/:businessUrl/care"
          element={<LegacyPathRedirect build={({ businessUrl }) => `/b/${businessUrl}/care`} />}
        />
        <Route
          path="/:businessUrl/care/referral"
          element={<LegacyPathRedirect build={({ businessUrl }) => `/b/${businessUrl}/care/referral`} />}
        />
        <Route
          path="/:businessUrl/care/track/:token"
          element={
            <LegacyPathRedirect
              build={({ businessUrl, token }) => `/b/${businessUrl}/care/track/${token}`}
            />
          }
        />
        <Route
          path="/embed/:businessUrl/booking/:bookingTypeLink"
          element={<ExternalTimelyRoute />}
        />
        {/* legacy booking paths */}
        <Route
          path="/:businessUrl/booking"
          element={
            <LegacyPathRedirect
              build={({ businessUrl }) => `/b/${businessUrl}/booking`}
            />
          }
        />
        <Route
          path="/:businessUrl/booking/:bookingTypeLink"
          element={
            <LegacyPathRedirect
              build={({ businessUrl, bookingTypeLink }) => `/b/${businessUrl}/booking/${bookingTypeLink}`}
            />
          }
        />
        <Route
          path="/:businessUrl/:staffUrl/:bookingTypeLink/:time"
          element={
            <LegacyPathRedirect
              build={({ businessUrl, staffUrl, bookingTypeLink, time }) =>
                `/b/${businessUrl}/${staffUrl}/${bookingTypeLink}/${time}`
              }
            />
          }
        />
        <Route
          path="/:businessUrl/booking/servicesConfirmed/:title/:timeRange/:time/:color/:staffUrl/:userName"
          element={
            <LegacyPathRedirect
              build={({ businessUrl, title, timeRange, time, color, staffUrl, userName }) =>
                `/b/${businessUrl}/booking/servicesConfirmed/${title}/${timeRange}/${time}/${color}/${staffUrl}/${userName}`
              }
            />
          }
        />

        {/* legacy staff root & wallet/tip */}
        <Route
          path="/:businessUrl/:staffUrl"
          element={
            <LegacyPathRedirect
              build={({ businessUrl, staffUrl }) => `/b/${businessUrl}/${staffUrl}`}
            />
          }
        />
        <Route
          path="/:businessUrl/:staffUrl/wallet"
          element={
            <LegacyPathRedirect
              build={({ businessUrl, staffUrl }) => `/b/${businessUrl}/${staffUrl}/wallet`}
            />
          }
        />
        <Route
          path="/:businessUrl/:staffUrl/tip"
          element={
            <LegacyPathRedirect
              build={({ businessUrl, staffUrl }) => `/b/${businessUrl}/${staffUrl}/tip`}
            />
          }
        />
        <Route
          path="/:businessUrl/tip"
          element={<LegacyPathRedirect build={({ businessUrl }) => `/b/${businessUrl}/tip`} />}
        />

        {/* ---------- Biz (owner) routes (unchanged) ---------- */}
        <Route
          path="/biz"
          element={
            token ? (
              <Navigate
                to={hasAnyBusiness ? postLoginRedirect : "/biz/business"}
                replace
              />
            ) : (
              <Navigate to={guestBizEntryRoute} replace />
            )
          }
        />
        {/* <Route
          path="/biz/pricing"
          element={
            <PayPalScriptProvider
              options={{
                "client-id": clientId,
                vault: true,
                intent: "subscription",
              }}
            >
              <PricingPage />
            </PayPalScriptProvider>
          }
        /> */}

        <Route
          path="/biz/products"
          element={
            <ProtectedComponent>
              <Navigate to={getRuntimeBusinessHomePath()} replace />
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/pricing"
          element={<PricingPage />}
        />


        <Route
          path="/login"
          element={!token ? <Login token={token} login={login} /> : <Navigate to={postLoginRedirect} replace />}
        />
        <Route
          path="/start-free-trial"
          element={
            !token ? (
              <SignUp token={token} login={login} />
            ) : (
              <Navigate to={postLoginRedirect} replace />
            )
          }
        />
        <Route
          path="/forgotpassword"
          element={!token ? <ForgotPassword token={token} login={login} /> : <Navigate to={postLoginRedirect} replace />}
        />
        <Route
          path="/biz/business/:tabId?"
          element={
            <ProtectedComponent>
              <PayPalScriptProvider
                options={{
                  "client-id": clientId,
                  vault: true,
                  intent: "subscription",
                }}
              >
                <BusinessPortal />
              </PayPalScriptProvider>
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/calendar"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/biz/capture"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/biz/printview"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/biz/approve/:token"
          element={<UpdateStaffStatus />}
        />
        <Route
          path="/signup"
          element={
            !token ? (
              <SignUp login={login} />
            ) : (
              <Navigate to={postLoginRedirect} replace />
            )
          }
        />
        <Route
          path="/biz/integrations/:integration"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/biz/bookingtypes"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/biz/create/:bookingtypecategory"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/biz/update/:id"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/biz/staff"
          element={
            <ProtectedComponent>
              <StaffGoogleCallbackRedirect>
                <StaffPortal />
              </StaffGoogleCallbackRedirect>
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/hiring"
          element={
            <ProtectedComponent>
              <Navigate to="/biz/manage/jobs?v=care" replace />
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/manage/jobs"
          element={
            <ProtectedComponent>
              <JobManagePortal />
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/marketplace"
          element={
            <ProtectedComponent>
              <MarketplacePortal />
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/integrations"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="/connect/google_calendar"
          element={<ExternalTimelyRoute />}
        />

        {/* Search & misc */}
        <Route path="/search" element={<SearchPage />} />
        <Route path="/category/:category" element={<CategoryPage />} />
        <Route path="/about" element={<PrivacyTermsAbout />} />
        <Route path="/privacy" element={<PrivacyTermsAbout />} />
        <Route path="/terms" element={<PrivacyTermsAbout />} />
        <Route path="/sms-opt-in" element={<PrivacyTermsAbout />} />
        <Route path="/hipaa-security" element={<PrivacyTermsAbout />} />
        <Route path="/faq" element={<PrivacyTermsAbout />} />
        <Route path="/web-design" element={<WebDesignPage />} />

        {/* Tips / Wallet (coins) */}
        <Route
          path="/coins/buy"
          element={
            <PayPalScriptProvider options={{ "client-id": clientId }}>
              <ProtectedComponentNeedToHaveBusiness>
                <TipProvider>
                  <BuyCoinsPage />
                </TipProvider>
              </ProtectedComponentNeedToHaveBusiness>
            </PayPalScriptProvider>
          }
        />
        <Route
          path="/myschedule"
          element={<ExternalTimelyRoute />}
        />
        <Route
          path="care/myjobs"
          element={
            <MyJobsPage />
          }
        />

        <Route
          path="/profile"
          element={<UserPage logout={logout} token={token} />}
        />
        <Route
          path="/biz/profile"
          element={
            <ProtectedComponent>
              <UserProfilePage logout={logout} token={token} />
            </ProtectedComponent>
          }
        />
        <Route
          path="/biz/subscription-success/:planName"
          element={
            <ProtectedComponent>
              <SubscriptionSuccessPage />
            </ProtectedComponent>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    ),
    [careBrandHost, clientId, guestBizEntryRoute, hasAnyBusiness, location.hash, location.pathname, location.search, login, logout, postLoginRedirect, providerUser, setImage, token]
  );

  const BusinessRoutes = useMemo(() => {
    if (!isCurrentPathEnabled) return <NotFound />;
    if (shouldGateRouteLoading && hasLoading) return <>{renderLoading()}</>;
    if (shouldGateRouteLoading && hasError) return <>{renderError()}</>;
    return <>{routes}</>;
  }, [hasLoading, hasError, isCurrentPathEnabled, routes, shouldGateRouteLoading]);

  return <RuntimeAppBoundary>{BusinessRoutes}</RuntimeAppBoundary>;
};

export default RouteGroups;
